/** Filtering, sorting, and role stats for Settings → Companies. */

import { normalizeDomain } from "./logo";
import type { Company, Job } from "./types";

export type CompanyStatusFilter = "all" | "active" | "paused" | "needs_ats" | "skipped";
export type CompanyDomainFilter = "all" | "has" | "missing";
export type CompanySort = "name" | "roles" | "recent" | "needs_ats";

export interface CompanyRoleStats {
  roleCount: number;
  lastSurfacedAt: string;
}

export function normalizeCompanyDomain(value: string): string {
  return normalizeDomain(value);
}

export function buildCompanyRoleStats(jobs: Job[]): Map<string, CompanyRoleStats> {
  const map = new Map<string, CompanyRoleStats>();
  for (const job of jobs) {
    const key = (job.company || "").toLowerCase();
    if (!key) continue;
    const added = job.added_at || "";
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { roleCount: 1, lastSurfacedAt: added });
      continue;
    }
    prev.roleCount += 1;
    if (added > prev.lastSurfacedAt) prev.lastSurfacedAt = added;
  }
  return map;
}

function matchesStatus(company: Company, filter: CompanyStatusFilter): boolean {
  if (filter === "all") return true;
  if (filter === "active") return !company.inactive;
  if (filter === "paused") return Boolean(company.inactive);
  if (filter === "needs_ats") {
    return company.ats === "unsupported" && company.resolution_status === "pending";
  }
  return company.ats === "unsupported" && company.resolution_status !== "pending";
}

function matchesDomain(company: Company, filter: CompanyDomainFilter): boolean {
  if (filter === "all") return true;
  const has = Boolean(normalizeCompanyDomain(company.domain || ""));
  return filter === "has" ? has : !has;
}

function matchesSearch(company: Company, q: string): boolean {
  if (!q) return true;
  const hay = `${company.name} ${company.slug} ${company.domain || ""}`.toLowerCase();
  return hay.includes(q);
}

function needsAtsRank(company: Company): number {
  if (company.ats === "unsupported" && company.resolution_status === "pending") return 0;
  if (company.ats === "unsupported") return 1;
  return 2;
}

export function filterAndSortCompanies(
  companies: Company[],
  opts: {
    search: string;
    status: CompanyStatusFilter;
    ats: string;
    domain: CompanyDomainFilter;
    sort: CompanySort;
    roleStats: Map<string, CompanyRoleStats>;
  },
): { company: Company; index: number }[] {
  const q = opts.search.trim().toLowerCase();
  const rows = companies
    .map((company, index) => ({ company, index }))
    .filter(({ company }) => {
      if (!matchesSearch(company, q)) return false;
      if (!matchesStatus(company, opts.status)) return false;
      if (opts.ats !== "all" && company.ats !== opts.ats) return false;
      if (!matchesDomain(company, opts.domain)) return false;
      return true;
    });

  const nameCmp = (a: Company, b: Company) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

  rows.sort((a, b) => {
    const ca = a.company;
    const cb = b.company;
    const sa = opts.roleStats.get(ca.name.toLowerCase());
    const sb = opts.roleStats.get(cb.name.toLowerCase());

    switch (opts.sort) {
      case "roles": {
        const ra = sa?.roleCount ?? 0;
        const rb = sb?.roleCount ?? 0;
        if (rb !== ra) return rb - ra;
        return nameCmp(ca, cb);
      }
      case "recent": {
        const ta = sa?.lastSurfacedAt ?? "";
        const tb = sb?.lastSurfacedAt ?? "";
        if (ta || tb) {
          if (!ta) return 1;
          if (!tb) return -1;
          if (tb !== ta) return tb.localeCompare(ta);
        }
        return nameCmp(ca, cb);
      }
      case "needs_ats": {
        const ra = needsAtsRank(ca);
        const rb = needsAtsRank(cb);
        if (ra !== rb) return ra - rb;
        return nameCmp(ca, cb);
      }
      case "name":
      default:
        return nameCmp(ca, cb);
    }
  });

  return rows;
}
